import { PrimaryButton } from '@components/Buttons/PrimaryButton';
import { ComboField } from '@components/Fields/ComboField';
import { FloatField } from '@components/Fields/FloatField';
import { StringField } from '@components/Fields/StringField';
import { ModalWindow } from '@components/Modal/ModalWindow';
import { ModalWindowActions } from '@components/Modal/ModalWindowActions';
import { ModalWindowContent } from '@components/Modal/ModalWindowContent';
import { ModalWindowTitle } from '@components/Modal/ModalWindowTitle';
import { useField } from '@hooks/useField';
import { useForm } from '@hooks/useForm';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { IModalWindow } from '@hooks/useModalWindow';
import { useValidation } from '@hooks/useValidation';
import { Grid } from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import { IComboEmpresa } from '@model/Empresa';
import { IVendedor } from '@model/Vendedor';
import { IVendedorComissao, VendedorComissaoTipoEnum } from '@model/VendedorComissao';
import { IRegistrarComisssaoRequest, useVendedorComissaoService } from '@services/VendedorComissaoService';
import { KeyOfEnum } from '@utils/TypeHelpers';
import { yup } from '@validations/Yup';
import React from 'react';

export type ComissaoRegistrarModalProps = {
    modal: IModalWindow
    vendedorComissaoTipo: VendedorComissaoTipoEnum
    onComissaoRegistrada: (vendedorComissao: IVendedorComissao) => void
    vendedor: IVendedor
    comboEmpresa?: IComboEmpresa
}

export function ComissaoRegistrarModal ({ modal, vendedorComissaoTipo, vendedor, onComissaoRegistrada, comboEmpresa }: ComissaoRegistrarModalProps) {
    const loading = useLoading();
    const message = useMessage();

    const vendedorComissaoService = useVendedorComissaoService();

    const form = useForm<IRegistrarComisssaoRequest>({
        empresaId: useField(),
        descricao: useField(""),
        valor: useField(0),
        vendedorComissaoTipo: useField(vendedorComissaoTipo)
    });

    const validation = useValidation<Partial<IRegistrarComisssaoRequest>>({
        descricao: yup.string().trim().required(),
        valor: yup.number().required().moreThan(0),
    });
    
    const handleConfirm = React.useCallback(() => {
        if (!loading.isLoading) {
            const values = {
                ...form.getInputValues(),
                vendedorComissaoTipo
            }

            form.clearErrors();
            validation.validate(values)
                .then(_ => {
                    loading.start();
                    vendedorComissaoService.registrar(vendedor.vendedorId, values)
                        .then(response => {
                            const prefix : KeyOfEnum<VendedorComissaoTipoEnum, string> = {
                                [VendedorComissaoTipoEnum.None]: "None",
                                [VendedorComissaoTipoEnum.Venda]: "Venda",
                                [VendedorComissaoTipoEnum.Debito]: "Débito",
                                [VendedorComissaoTipoEnum.Credito]: "Crédito",
                                [VendedorComissaoTipoEnum.Pagamento]: "Pagamento",
                            }

                            message.success(`${prefix[vendedorComissaoTipo]} registrado com sucesso`)
                            onComissaoRegistrada(response.data);

                            if (response.data.tipo.value === VendedorComissaoTipoEnum.Pagamento) {
                                window.open(`/PagamentoComissaoRecibo/${response.data.vendedorComissaoId}`);
                            }

                            modal.close();
                        })
                        .catch(e => {
                            message.handleResponse(e)
                                .badRequest(form.setErrorsFromServer)
                                .showException();
                        })
                        .finally(loading.stop);
                })
                .catch(form.setErrorsFromYup);
        }
    }, [form, loading, message, modal, onComissaoRegistrada, validation, vendedor.vendedorId, vendedorComissaoService, vendedorComissaoTipo]);

    return (
        <ModalWindow
            modal={modal}
            disabled={loading.isLoading}
            TransitionProps={{
                onEnter: form.reset
            }}
            size={comboEmpresa !== undefined ? "md" : "xs"}
        >
            <ModalWindowTitle
                icon={vendedorComissaoTipo === VendedorComissaoTipoEnum.Credito ? <AddCircleIcon style={{color: 'darkgreen'}} /> : (vendedorComissaoTipo === VendedorComissaoTipoEnum.Debito ? <RemoveCircleIcon color="secondary" /> : <AddCircleIcon />)}
                title={vendedorComissaoTipo === VendedorComissaoTipoEnum.Credito ? "Registrar Crédito de Comissão" : (vendedorComissaoTipo === VendedorComissaoTipoEnum.Debito ? "Registrar Débito de Comissão" : "Registrar Pagamento de Comissão")}
            />

            <ModalWindowContent>
                <Grid container spacing={1}>
                    {
                        comboEmpresa !== undefined && (
                            <Grid item xs={3}>
                                <ComboField 
                                    field={form.fields.empresaId}
                                    label="Empresa"
                                    required
                                >
                                    {
                                        comboEmpresa.createOptions()
                                    }
                                </ComboField>
                            </Grid>
                        )
                    }

                    <Grid item xs={comboEmpresa !== undefined ? 6 : 9}>
                        <StringField 
                            field={form.fields.descricao}
                            label="Descrição"
                            required
                        />
                    </Grid>

                    <Grid item xs={3}>
                        <FloatField
                            field={form.fields.valor}
                            label="Valor"
                            required
                        />
                    </Grid>
                </Grid>                
            </ModalWindowContent>

            <ModalWindowActions>
                <PrimaryButton onClick={handleConfirm}>{vendedorComissaoTipo === VendedorComissaoTipoEnum.Credito ? "Registrar Crédito" : (vendedorComissaoTipo === VendedorComissaoTipoEnum.Debito ? "Registrar Débito" : "Registrar Pagamento")}</PrimaryButton>
            </ModalWindowActions>
        </ModalWindow>
    )
}